import {
  AppBar,
  Backdrop,
  Toolbar,
  IconButton,
  InputBase,
  Box,
  Avatar,
  Typography,
  Badge,
  CircularProgress,
  Divider,
  ListItemText,
  MenuItem,
  Menu,
  Tooltip,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import NotificationsOffRoundedIcon from "@mui/icons-material/NotificationsOffRounded";
import ButtonComp from "./Button";
import { string } from "../constants/string";
import { useEffect, useRef, useState } from "react";
import { useUserStore } from "../store/useUserStore";
import CollapseMenu from "./CollapseMenu";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore";
import { formatUserRole } from "../utils/formatUserRole";
import {
  getNotifications,
  markNotificationAsRead,
} from "../services/dashboard";
import { useClaimDetailsStore } from "../store/useClaimDetailsStore";
import CloseIcon from "@mui/icons-material/Close";
import { signOut } from "@aws-amplify/auth";
import { beginSignOut, clearClientSession } from "../services/session";
import { getHostedLogoutUrl } from "../sso/config";
import { useClaimTypeStore } from "../store/useClaimTypeStore";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { isIfasDriverRole } from "../utils/portal";
import { canAccessIfasModule, hasIfasCapability } from "../utils/ifasAccess";
import { connectNotificationStream } from "../services/notification-stream";
import {
  NotificationItem,
  useNotificationStore,
} from "../store/useNotificationStore";
import { toast } from "react-toastify";
import {
  initializeNotificationSound,
  playNotificationChime,
} from "../services/notification-sound";

const SOUTH_AFRICA_TIME_ZONE = "Africa/Johannesburg";

const parseNotificationTimestamp = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  const hasExplicitZone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(trimmedValue);
  if (hasExplicitZone) {
    const zonedDate = new Date(trimmedValue);
    return Number.isNaN(zonedDate.getTime()) ? null : zonedDate;
  }

  const candidates = [
    new Date(`${trimmedValue}Z`),
    new Date(trimmedValue),
    new Date(`${trimmedValue}+02:00`),
  ].filter((candidate) => !Number.isNaN(candidate.getTime()));

  if (candidates.length === 0) {
    return null;
  }

  const now = Date.now();
  return candidates.reduce((best, candidate) =>
    Math.abs(candidate.getTime() - now) < Math.abs(best.getTime() - now)
      ? candidate
      : best,
  );
};

type SearchBarProps = {
  onNewClaimClick?: () => void;
  onNewCaseClick?: () => void;
  onCreateCateringClick?: () => void;
  onNewQuotationClick?: () => void;
  handleAddDriverClick?: () => void;
  handleAddVehicleClick?: () => void;
  onFilterMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
  handleSearch: (value: string) => any;
  userName?: string | undefined;
  userRole?: any;
  getSerachValue: (value: any) => void;
  searchText: string;
  showSearchInput: boolean;
  showFilterIcon: boolean;
  showHamburger?: boolean; // ✅ Added
  newClaimClick: boolean;
  viewMode: string | undefined;
  onHamburgerClick?: () => void; // ✅ Optional callback for drawer open
};

export default function SearchBar(props: SearchBarProps) {
  const {
    onNewClaimClick,
    onNewCaseClick,
    onCreateCateringClick,
    onNewQuotationClick,
    handleAddDriverClick,
    handleAddVehicleClick,
    onFilterMenuClick,
    userName,
    userRole,
    handleSearch,
    getSerachValue,
    searchText,
    showSearchInput,
    showFilterIcon,
    showHamburger = false, // ✅ Default false
    newClaimClick,
    viewMode,
    onHamburgerClick,
  } = props;

  const [showCollapse, setShowCollapse] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  // Ref, not state: two clicks in the same render tick would both read a
  // stale `false` from state and re-enter the sign-out.
  const signingOutRef = useRef(false);
  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<null | HTMLElement>(null);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const { first_name, last_name, user_name, userId, email } = useAuthStore();
  const role = useUserStore((state) => state.role);
  const claimType = useClaimTypeStore((state) => state.claimType);
  const notifications = useNotificationStore((state) => state.notifications);
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const isNotificationLoading = useNotificationStore(
    (state) => state.isLoading,
  );
  const isStreamConnected = useNotificationStore(
    (state) => state.isStreamConnected,
  );
  const setNotifications = useNotificationStore((state) => state.setNotifications);
  const prependNotification = useNotificationStore(
    (state) => state.prependNotification,
  );
  const markRead = useNotificationStore((state) => state.markNotificationRead);
  const setNotificationLoading = useNotificationStore(
    (state) => state.setLoading,
  );
  const setStreamConnected = useNotificationStore(
    (state) => state.setStreamConnected,
  );
  const clearNotifications = useNotificationStore(
    (state) => state.clearNotifications,
  );
  const effectiveRole = userRole || role;
  const isNotificationDisabled = (() => {
    if (!effectiveRole) return false;
    const normalized = String(effectiveRole).trim().toLowerCase().replace(/[\s-]+/g, "_");

    // Do not block IFAS-specific roles (e.g., ifas_removal_consultant, ifas_administrator, etc.)
    if (
      normalized.startsWith("ifas_") ||
      normalized.startsWith("ifaas_") ||
      normalized === "ifas_removal_consultant" ||
      normalized === "ifas_administrator"
    ) {
      return false;
    }

    const blockedRoles = [
      "consultant",
      "administrator",
      "finance",
      "manager",
      "manger",
      "underwriter",
      "under_writer"
    ];
    return blockedRoles.includes(normalized);
  })();
  const isDriverRole = isIfasDriverRole(effectiveRole);
  const canCreateIfasCase =
    canAccessIfasModule(effectiveRole, "casePortal") &&
    hasIfasCapability(effectiveRole, "caseInitialCreate");
  const canCreateQuotation =
    canAccessIfasModule(effectiveRole, "quotations") &&
    hasIfasCapability(effectiveRole, "quotationWrite");
  const canManageDrivers = canAccessIfasModule(effectiveRole, "drivers");
  const canManageVehicles = canAccessIfasModule(effectiveRole, "vehicles");
  const navigate = useNavigate();
  const isNotificationMenuOpen = Boolean(notificationAnchorEl);
  const visibleNotifications = notifications.filter((item) => !item.isRead);

  const getDisplayName = (
    first_name?: string | null,
    last_name?: string | null,
    user_name?: string,
  ) => {
    if (first_name && last_name) return `${first_name} ${last_name}`;
    if (first_name) return first_name;
    if (last_name) return last_name;
    return user_name || "";
  };

  const formatNotificationTime = (createdOn?: string | null) => {
    const parsedDate = parseNotificationTimestamp(createdOn);
    if (!parsedDate) {
      return "Just now";
    }

    const now = new Date();
    const diffMs = now.getTime() - parsedDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;

    return parsedDate.toLocaleString("en-ZA", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: SOUTH_AFRICA_TIME_ZONE,
    });
  };

  const buildNotificationMeta = (notification: NotificationItem) => {
    const parts = [];
    if (notification.referenceNumber) {
      parts.push(notification.referenceNumber);
    }
    if (notification.caseNumber) {
      parts.push(notification.caseNumber);
    }
    return parts.join(" • ");
  };

  // ✅ Severity-based visual treatment so SLA breaches stand out from routine approvals
  const getNotificationVisual = (notification: NotificationItem) => {
    const type = String(notification.notificationType || "").toUpperCase();
    const title = (notification.title || "").toLowerCase();

    if (type.includes("SLA") || title.includes("sla breached")) {
      return {
        icon: <WarningAmberRoundedIcon sx={{ fontSize: 18 }} />,
        color: "#DC2626",
        bg: "#FEF2F2",
        ring: "#FECACA",
        label: "Urgent",
      };
    }
    if (title.includes("approval")) {
      return {
        icon: <TaskAltRoundedIcon sx={{ fontSize: 18 }} />,
        color: "#EA580C",
        bg: "#FFF7ED",
        ring: "#FED7AA",
        label: "Action needed",
      };
    }
    return {
      icon: <NotificationsNoneRoundedIcon sx={{ fontSize: 18 }} />,
      color: "#2563EB",
      bg: "#EFF6FF",
      ring: "#BFDBFE",
      label: "Update",
    };
  };

  const clearSearch = () => {
    const setSearchedClaim = useClaimDetailsStore.getState().setSearchedClaim;
    setSearchedClaim(null);
  };

  const menuItems = [
    {
      label: isSigningOut ? "Signing out..." : "Logout",
      onClick: async () => {
        // A second click must not re-enter: it would issue a second
        // GlobalSignOut and race the navigation the first click already owns.
        // A ref, not state, so two clicks in the same render tick cannot both
        // read a stale `false`.
        if (signingOutRef.current) {
          return;
        }
        signingOutRef.current = true;
        setIsSigningOut(true);
        // The menu unmounts on close, so the "Signing out..." label above never
        // gets to paint. The overlay rendered below — outside the collapse — is
        // the durable feedback that stops the click reading as ignored while
        // the hosted logout is in flight, which is what made users click twice.
        setShowCollapse(false);
        // From here until the browser leaves, this logout owns the navigation.
        // The dashboards poll on a timer, so a 401 lands mid-logout routinely;
        // the interceptor must not hard-redirect out from under us.
        beginSignOut();
        toast.info("Signing you out...", { toastId: "signing-out" });

        useNotificationStore.getState().clearNotifications();

        // Best effort: revoke the refresh token server-side. Amplify already
        // swallows GlobalSignOut failures internally, and a failure here must
        // never stop us ending the hosted session, so it cannot abort the
        // sequence — we always continue to the teardown and navigation below.
        try {
          await signOut({ global: true });
        } catch (err) {
          console.error("Error revoking tokens during logout", err);
        }

        // Deterministic teardown, then exactly one navigation — no wall-clock
        // fallback. Navigating to the Cognito hosted logout ourselves is what
        // actually ends the hosted session; it returns the browser to
        // REDIRECT_SIGNOUT (the /logged-out screen, never "/"), which clears
        // the client session again on arrival.
        //
        // This previously waited 2s and then navigated locally to /logged-out.
        // `signOut()` resolves as soon as Amplify *assigns* window.location,
        // not once the browser has left, so on a slow connection that timer
        // fired first and aborted the still-uncommitted redirect to Cognito —
        // the hosted session survived and the next sign-in silently re-
        // authenticated, so the user had to log out twice (ICE-BC-BUG-018).
        clearClientSession();
        window.location.assign(getHostedLogoutUrl());
      },
    },
  ];

  const handleToggle = () => {
    setShowCollapse((prev) => !prev);
  };

  const handleNotificationMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    const recipientUserId = userId || undefined;
    const recipientEmail = email || undefined;

    if (!notification.isRead) {
      const response = await markNotificationAsRead(notification.id, {
        recipientUserId,
        recipientEmail,
      });

      if (response?.status === 200) {
        markRead(notification.id);
      }
    }

    handleNotificationMenuClose();

    const type = String(notification.notificationType || "");
    const notificationContext = `${notification.title || ""} ${notification.message || ""}`.toLowerCase();
    const caseNo = notification.caseNumber || notification.referenceNumber;

    if (type === "DRIVER_FLEET_ASSIGNMENT" || type === "DRIVER_FLEET_REASSIGNED"
      || (isDriverRole && type === "BODY_REMOVAL_ASSIGNMENT")) {
      navigate("/dashboard", { state: { viewMode: "DRIVERS" } });
    } else if (type === "BODY_REMOVAL_ASSIGNMENT") {
      navigate("/dashboard", { state: { viewMode: "BODY-REMOVAL" } });
    } else if (type === "FUNERAL_FLEET_REQUESTED"
      || type === "FUNERAL_FLEET_ALLOCATED"
      || (type === "FUNERAL_CONFIRMED_HANDOFF" && notificationContext.includes("fleet"))) {
      navigate("/dashboard", {
        state: {
          viewMode: "FLEET",
          // Fleet-request notifications land on the Fleet Requests destination. This was
          // typed against FleetPortal's FleetTab, which left with IFAS (#2089); IFAS
          // accounts are sent to their own portal, so this branch no longer runs here.
          fleetTab: "requests",
          fleetRequestReference: notification.referenceNumber,
          caseNumber: notification.caseNumber,
        },
      });
    } else if (type.startsWith("WAREHOUSE_")) {
      navigate("/dashboard", { state: { viewMode: "WAREHOUSE" } });
    } else if (type.startsWith("BODY_MOVEMENT_")) {
      navigate("/dashboard", { state: { viewMode: "BODY_DISPATCH" } });
    } else if (type === "POST_FUNERAL_FEEDBACK") {
      navigate("/dashboard", { state: { viewMode: "FUNERAL_PLANNING" } });
    } else if (caseNo) {
      navigate("/dashboard", { state: { viewMode: "CASE_DETAILS", caseNumber: caseNo } });
    }
  };

  // ✅ Mark all as read — reuses the same single-notification API
  const handleMarkAllAsRead = async (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    event.stopPropagation();
    const recipientUserId = userId || undefined;
    const recipientEmail = email || undefined;

    if (visibleNotifications.length === 0 || isMarkingAllRead) return;

    setIsMarkingAllRead(true);
    try {
      await Promise.all(
        visibleNotifications.map(async (notification) => {
          const response = await markNotificationAsRead(notification.id, {
            recipientUserId,
            recipientEmail,
          });
          if (response?.status === 200) {
            markRead(notification.id);
          }
        }),
      );
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setShowCollapse(false);
      }
    }

    if (showCollapse) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCollapse]);

  useEffect(() => {
    initializeNotificationSound();
  }, []);

  useEffect(() => {
    if (isNotificationDisabled) {
      clearNotifications();
      return;
    }

    const recipientUserId = userId?.trim();
    const recipientEmail = email?.trim();

    if (!recipientUserId && !recipientEmail) {
      clearNotifications();
      return;
    }

    let mounted = true;

    const fetchInitialNotifications = async () => {
      setNotificationLoading(true);
      const response = await getNotifications({
        recipientUserId,
        recipientEmail,
        unreadOnly: true,
        size: 20,
      });

      if (!mounted) {
        return;
      }

      if (response?.status === 200) {
        setNotifications(
          response.data?.notifications ?? [],
          response.data?.unreadCount ?? 0,
        );
      }

      setNotificationLoading(false);
    };

    void fetchInitialNotifications();

    const disconnect = connectNotificationStream({
      recipientUserId,
      recipientEmail,
      onNotification: (notification) => {
        const alreadyPresent = useNotificationStore
          .getState()
          .notifications.some((item) => item.id === notification.id);

        prependNotification(notification);

        if (!alreadyPresent) {
          playNotificationChime();
          toast.info(
            notification.title || notification.message || "New notification",
            {
              toastId: notification.id,
              autoClose: 5000,
              closeOnClick: true,
              pauseOnHover: true,
            },
          );
        }
      },
      onConnectionStateChange: (isConnected) => {
        if (mounted) {
          setStreamConnected(isConnected);
        }
      },
      onError: (error) => {
        if (mounted) {
          console.error("Notification stream error", error);
        }
      },
    });

    return () => {
      mounted = false;
      disconnect();
    };
  }, [
    userId,
    email,
    clearNotifications,
    prependNotification,
    setNotificationLoading,
    setNotifications,
    setStreamConnected,
    isNotificationDisabled,
  ]);

  return (
    <>
    <AppBar
      sx={{
        height: { xs: "64px", md: "80px" },
        width: "100%",
        backgroundColor: "background.default",
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
      }}
      position="static"
      elevation={0}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          minWidth: 0,
          px: { xs: 1, sm: 2 },
          py: { xs: 0.5, sm: 1 },
        }}
      >
        {showHamburger && (
          // Responsive pass (F.9): below md the Sidebar renders its own
          // hamburger, which opens the nav Drawer. This one toggles whether
          // the Sidebar subtree renders at all, so the two stacked up as
          // indistinguishable duplicate controls on tablet/handheld. It is
          // the desktop dock collapse control, so it belongs to md and up.
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
            <IconButton
              onClick={onHamburgerClick}
              aria-label="Collapse navigation"
              sx={{ width: 44, height: 44 }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        {showFilterIcon && role !== "underwriter" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={onFilterMenuClick}
              aria-label="Open filters"
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                border: "1px solid #E5E7EB",
                backgroundColor: "#FFFFFF",
                color: "#4B5563",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#FFF7ED",
                  boxShadow: "0 2px 8px rgba(249,115,22,0.12)",
                },
              }}
            >
              <FilterAltOutlinedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Box>
        )}

        {/* Search Box */}
        {showSearchInput && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f5f5f5",
              borderRadius: "10px",
              px: 2,
              py: 0.5,
              width: { xs: "280px", sm: "300px", md: "400px" },
              maxWidth: "100%",
              textAlign: "left",
              ml: showFilterIcon ? 2 : 0,
            }}
          >
            <SearchIcon sx={{ color: "gray", mr: 1 }} />
            <InputBase
              placeholder="Search"
              value={searchText}
              onChange={(e) => {
                const val = e.target.value;
                getSerachValue(val);
                if (val.trim() === "") {
                  clearSearch();
                }
              }}
              fullWidth
              sx={{
                fontSize: "14px",
                color: "text.primary",
                minWidth: 0,
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch(searchText);
              }}
            />
            {searchText && (
              <IconButton
                size="small"
                onClick={() => {
                  getSerachValue("");
                  clearSearch();
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
            <ButtonComp
              variant="contained"
              height={30}
              textTransform="none"
              color="secondary"
              fontSize="14px"
              onClick={() => handleSearch(searchText)}
              sx={{ flexShrink: 0 }}
            >
              Search
            </ButtonComp>
          </Box>
        )}

        {/* Right Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
            marginLeft: { xs: 0, md: "auto" },
          }}
        >
          {claimType ? (
            role !== "finance" &&
            role !== "underwriter" &&
            !newClaimClick && (
              <ButtonComp
                variant="contained"
                height={36}
                width={140}
                textTransform="none"
                color="secondary"
                fontSize="14px"
                onClick={onNewClaimClick}
              >
                {string.searchbar.newClaim}
              </ButtonComp>
            )
          ) : viewMode === "CASE-PORTAL" && canCreateIfasCase ? (
            <ButtonComp
              variant="contained"
              height={36}
              width={140}
              textTransform="none"
              color="secondary"
              fontSize="14px"
              onClick={onNewCaseClick}
            >
              New Case
            </ButtonComp>
          ) : viewMode === "QUOTATIONS" && canCreateQuotation ? (
            <ButtonComp
              variant="contained"
              height={36}
              width={150}
              textTransform="none"
              color="secondary"
              fontSize="14px"
              onClick={onNewQuotationClick}
            >
              New Quotation
            </ButtonComp>
          ) : viewMode === "DRIVERS" && !isDriverRole && canManageDrivers ? (
            <ButtonComp
              variant="contained"
              height={36}
              width={150}
              textTransform="none"
              color="secondary"
              fontSize="14px"
              onClick={handleAddDriverClick}
            >
              Add Driver
            </ButtonComp>
          ) : viewMode === "VEHICLES" && canManageVehicles ? (
            <ButtonComp
              variant="contained"
              height={36}
              width={150}
              textTransform="none"
              color="secondary"
              fontSize="14px"
              onClick={handleAddVehicleClick}
            >
              Add Vehicle
            </ButtonComp>
          ) : null}

          {!isNotificationDisabled && (
            <Tooltip
              title={isStreamConnected ? "Notifications live" : "Notifications"}
            >
              <IconButton
                onClick={handleNotificationMenuOpen}
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#FFFFFF",
                  color: unreadCount > 0 ? "#EA580C" : "#4B5563",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#FFF7ED",
                    boxShadow: "0 2px 8px rgba(249,115,22,0.12)",
                  },
                }}
              >
                <Badge
                  badgeContent={unreadCount > 99 ? "99+" : unreadCount}
                  color="error"
                >
                  <NotificationsNoneRoundedIcon sx={{ fontSize: 22 }} />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ position: "relative" }} ref={userMenuRef}>
            <Box
              onClick={handleToggle}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "primary.main",
                  width: 40,
                  height: 40,
                  fontSize: 16,
                }}
              >
                {first_name?.charAt(0).toUpperCase() ||
                  user_name?.charAt(0).toUpperCase()}
              </Avatar>

              <Box sx={{ textAlign: "left" }}>
                <Typography variant="h6" fontWeight={500} textAlign="left">
                  {formatUserRole(
                    getDisplayName(first_name, last_name, user_name),
                  )}
                </Typography>

                <Typography variant="caption" color="text.disabled">
                  {formatUserRole(role ? role : userRole)}
                </Typography>
              </Box>
              {showCollapse ? (
                <ExpandLessIcon sx={{ color: "text.secondary" }} />
              ) : (
                <ExpandMoreIcon sx={{ color: "text.secondary" }} />
              )}
            </Box>
            <CollapseMenu open={showCollapse} items={menuItems} />
          </Box>
        </Box>
      </Toolbar>

      <Menu
        anchorEl={notificationAnchorEl}
        open={isNotificationMenuOpen}
        onClose={handleNotificationMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              maxWidth: "calc(100vw - 32px)",
              borderRadius: "16px",
              mt: 1,
              border: "1px solid #F1F5F9",
              boxShadow: "0 20px 45px rgba(15,23,42,0.14)",
              overflow: "hidden",
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            background:
              "linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#0F172A" }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Box
                sx={{
                  backgroundColor: "#FEF2F2",
                  color: "#DC2626",
                  fontSize: "11px",
                  fontWeight: 700,
                  borderRadius: "999px",
                  px: 1,
                  py: 0.25,
                  lineHeight: 1.4,
                }}
              >
                {unreadCount} unread
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: isStreamConnected ? "#10B981" : "#CBD5E1",
                  boxShadow: isStreamConnected
                    ? "0 0 0 3px rgba(16,185,129,0.15)"
                    : "none",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: isStreamConnected ? "#047857" : "text.secondary",
                  fontWeight: 600,
                }}
              >
                {isStreamConnected ? "Live" : "Reconnecting"}
              </Typography>
            </Box>
          </Box>
        </Box>

        {visibleNotifications.length > 0 && (
          <Box
            sx={{
              px: 2,
              pb: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="small"
              startIcon={<DoneAllRoundedIcon sx={{ fontSize: 15 }} />}
              onClick={handleMarkAllAsRead}
              disabled={isMarkingAllRead}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 600,
                color: "#EA580C",
                minWidth: 0,
                px: 1,
                py: 0.25,
                "&:hover": { backgroundColor: "#FFF7ED" },
              }}
            >
              {isMarkingAllRead ? "Marking..." : "Mark all read"}
            </Button>
          </Box>
        )}

        <Divider />

        {/* Body */}
        {isNotificationLoading ? (
          <Box
            sx={{
              px: 2,
              py: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={24} sx={{ color: "#EA580C" }} />
          </Box>
        ) : visibleNotifications.length === 0 ? (
          <Box
            sx={{
              px: 2,
              py: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "14px",
                backgroundColor: "#F8FAFC",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NotificationsOffRoundedIcon
                sx={{ fontSize: 24, color: "#CBD5E1" }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ color: "#0F172A", fontWeight: 600 }}
            >
              You're all caught up
            </Typography>
            <Typography variant="caption" color="text.secondary">
              New notifications will show up here.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: "#F8FAFC",
              p: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxHeight: 420,
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#E2E8F0",
                borderRadius: "8px",
              },
            }}
          >
            {visibleNotifications.map((notification) => {
              const visual = getNotificationVisual(notification);
              return (
                <MenuItem
                  key={notification.id}
                  onClick={() => void handleNotificationClick(notification)}
                  sx={{
                    alignItems: "flex-start",
                    gap: 1.25,
                    py: 1.25,
                    px: 1.5,
                    borderRadius: "12px",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #F1F5F9",
                    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                    position: "relative",
                    whiteSpace: "normal",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      backgroundColor: "#FFFFFF",
                      border: `1px solid ${visual.ring}`,
                      boxShadow: "0 4px 14px rgba(15,23,42,0.08)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {/* unread dot */}
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: visual.color,
                      position: "absolute",
                      top: 14,
                      right: 12,
                    }}
                  />

                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "10px",
                      backgroundColor: visual.bg,
                      border: `1px solid ${visual.ring}`,
                      color: visual.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {visual.icon}
                  </Box>

                  <ListItemText
                    sx={{ m: 0 }}
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                          pr: 1.5,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: "#0F172A",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            lineHeight: 1.35,
                          }}
                        >
                          {notification.title || "Notification"}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: "#94A3B8",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            mt: "1px",
                          }}
                        >
                          {formatNotificationTime(notification.createdOn)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        {buildNotificationMeta(notification) ? (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              fontSize: "11px",
                              fontWeight: 700,
                              color: visual.color,
                              backgroundColor: visual.bg,
                              borderRadius: "6px",
                              px: 0.75,
                              py: 0.25,
                              mb: 0.5,
                            }}
                          >
                            {buildNotificationMeta(notification)}
                          </Box>
                        ) : null}
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748B",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            lineHeight: 1.5,
                            fontSize: "13px",
                          }}
                        >
                          {notification.message ||
                            "Open to view more details."}
                        </Typography>
                      </Box>
                    }
                  />
                </MenuItem>
              );
            })}
          </Box>
        )}
      </Menu>
    </AppBar>
    {/*
      Durable sign-out feedback. The user menu unmounts on close, so the
      "Signing out..." label inside it never paints; handing off to the Cognito
      hosted logout then takes a visible moment with nothing changing on screen,
      which is what made the first click read as ignored. Rendering here — a
      sibling of the AppBar, outside the collapse — keeps the state visible for
      the whole round trip, and blocks further input while it is in flight.
    */}
    <Backdrop
      open={isSigningOut}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        color: "#FFFFFF",
        backgroundColor: "rgba(15,23,42,0.55)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <CircularProgress color="inherit" size={36} />
      <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
        Signing you out…
      </Typography>
    </Backdrop>
    </>
  );
}
